import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Upload, Trash2, LogOut, GripVertical, Loader2, ArrowLeft, ShieldCheck, LayoutDashboard, ClipboardList, Images, ShieldAlert, Settings as SettingsIcon } from "lucide-react";
import { LOGO_URL } from "@/config";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { API, abs } from "@/pages/admin/shared";
import Dashboard from "@/pages/admin/Dashboard";
import RequestsPanel from "@/pages/admin/RequestsPanel";
import SecurityLog from "@/pages/admin/SecurityLog";
import Settings from "@/pages/admin/Settings";

const TOKEN_KEY = "sedlo_admin_token";

export default function AdminGallery() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Admin Panel | Sedlo Services";
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { setEmail(data.email || ""); setChecking(false); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setChecking(false); });
  }, [token]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#060a12] text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  return token
    ? <Manager token={token} email={email} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />
    : <LoginForm onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
}

function LoginForm({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Prihlásenie zlyhalo.");
    } finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, reset_base: `${window.location.origin}/admin/reset` });
      toast.success("Ak e-mail existuje, poslali sme naň odkaz na obnovenie hesla.");
      setMode("login");
    } catch {
      toast.success("Ak e-mail existuje, poslali sme naň odkaz na obnovenie hesla.");
      setMode("login");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#060a12] px-4">
      <form onSubmit={mode === "login" ? submit : sendReset} data-testid="admin-login-form" className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <img src={LOGO_URL} alt="Sedlo Services" className="h-12 w-auto mx-auto mb-6" />
        <div className="flex items-center justify-center gap-2 text-navy mb-6">
          <ShieldCheck className="w-5 h-5 text-brand" />
          <h1 className="font-head text-xl font-semibold">{mode === "login" ? "Admin Panel" : "Obnovenie hesla"}</h1>
        </div>
        <label className="block text-sm font-medium text-navy mb-1">E-mail</label>
        <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          className="w-full mb-4 px-4 py-2.5 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
        {mode === "login" && (
          <>
            <label className="block text-sm font-medium text-navy mb-1">Heslo</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full mb-2 px-4 py-2.5 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
            <button type="button" data-testid="admin-forgot-link" onClick={() => { setMode("forgot"); setError(""); }}
              className="text-sm text-brand hover:underline mb-4 block text-right w-full">Zabudli ste heslo?</button>
          </>
        )}
        {mode === "forgot" && (
          <p className="text-sm text-slate-500 mb-4">Zadajte svoj admin e-mail a pošleme naň odkaz na nastavenie nového hesla (platí 30 minút).</p>
        )}
        {error && <p data-testid="admin-login-error" className="text-sm text-red-600 mb-4">{error}</p>}
        <button data-testid="admin-login-submit" disabled={loading}
          className="btn-lift w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-md disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === "login" ? "Prihlásiť sa" : "Poslať odkaz")}
        </button>
        {mode === "forgot" ? (
          <button type="button" onClick={() => { setMode("login"); setError(""); }} className="mt-5 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-brand w-full">
            <ArrowLeft className="w-4 h-4" /> Späť na prihlásenie
          </button>
        ) : (
          <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-brand">
            <ArrowLeft className="w-4 h-4" /> Späť na web
          </Link>
        )}
      </form>
      <Toaster position="top-center" />
    </div>
  );
}

const MENU = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "requests", label: "Servisné požiadavky", icon: ClipboardList },
  { key: "gallery", label: "Galéria", icon: Images },
  { key: "security", label: "Bezpečnostný denník", icon: ShieldAlert },
  { key: "settings", label: "Nastavenia", icon: SettingsIcon },
];

function Manager({ token, email, onLogout }) {
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="min-h-screen bg-[#060a12] text-white" data-testid="admin-manager">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Sedlo Services" className="h-11 w-auto bg-white rounded-md px-2 py-1" />
            <span className="font-head font-semibold hidden sm:block">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/70 hover:text-white">Zobraziť web</Link>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-md">
              <LogOut className="w-4 h-4" /> Odhlásiť
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row max-w-7xl mx-auto">
        <nav className="md:w-60 md:shrink-0 border-b md:border-b-0 md:border-r border-white/10 p-3 flex md:flex-col gap-1 overflow-x-auto">
          {MENU.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`menu-${key}`} onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${tab === key ? "bg-brand text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Icon className="w-4 h-4 shrink-0" /> {label}
            </button>
          ))}
        </nav>

        <main className="flex-1 min-w-0 px-4 sm:px-6 py-8">
          {tab === "dashboard" && <Dashboard token={token} />}
          {tab === "requests" && <RequestsPanel token={token} />}
          {tab === "gallery" && <GalleryPanel token={token} />}
          {tab === "security" && <SecurityLog token={token} />}
          {tab === "settings" && <Settings token={token} email={email} />}
        </main>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}

function GalleryPanel({ token }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => axios.get(`${API}/gallery`).then(({ data }) =>
    setItems(data.map((d) => ({ id: d.id, src: abs(d.url) })))).catch(() => setItems([])), []);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const ok = [];
    const failed = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/admin/gallery`, fd, authHeader);
        ok.push(f.name);
      } catch (err) {
        const detail = err?.response?.data?.detail || "neznáma chyba";
        failed.push(`${f.name} (${detail})`);
      }
    }
    await load();
    if (ok.length) toast.success(`Nahraté: ${ok.length} ${ok.length === 1 ? "fotka" : "fotiek"}`);
    if (failed.length) toast.error(`Nepodarilo sa nahrať ${failed.length}: ${failed.join("; ")}`);
    setUploading(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Naozaj zmazať túto fotku?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, authHeader);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Fotka zmazaná");
    } catch { toast.error("Mazanie zlyhalo."); }
  };

  const persistOrder = async (arr) => {
    try { await axios.put(`${API}/admin/gallery/order`, { ids: arr.map((x) => x.id) }, authHeader); }
    catch { toast.error("Uloženie poradia zlyhalo."); }
  };

  const onDrop = (index) => {
    const from = dragIndex.current;
    setDragOver(null);
    if (from === null || from === index) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(index, 0, moved);
    setItems(arr);
    dragIndex.current = null;
    persistOrder(arr);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-head text-2xl font-semibold">Galéria</h1>
          <p className="text-white/60 text-sm mt-1">Nahrajte, zmažte alebo potiahnutím zmeňte poradie. Zmeny sú okamžite naživo.</p>
        </div>
        <label data-testid="admin-upload-label" className="btn-lift cursor-pointer inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-md shrink-0">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          {uploading ? "Nahrávam…" : "Nahrať fotky"}
          <input data-testid="admin-upload-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading}
            onChange={(e) => onUpload(e.target.files)} />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-white/50">
          Zatiaľ žiadne fotky. Nahrajte prvú fotku tlačidlom vyššie.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((img, i) => (
            <div
              key={img.id}
              data-testid={`admin-thumb-${i}`}
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
              onDrop={() => onDrop(i)}
              onDragEnd={() => setDragOver(null)}
              className={`group relative aspect-square overflow-hidden rounded-lg border ${dragOver === i ? "border-brand ring-2 ring-brand" : "border-white/10"} bg-white/5`}
            >
              <img src={img.src} alt="" className="w-full h-full object-cover pointer-events-none" />
              <div className="absolute top-2 left-2 bg-black/50 rounded p-1 cursor-grab active:cursor-grabbing">
                <GripVertical className="w-4 h-4 text-white/90" />
              </div>
              <button
                data-testid={`admin-delete-${i}`}
                onClick={() => onDelete(img.id)}
                className="absolute top-2 right-2 bg-red-600/90 hover:bg-red-600 rounded p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Zmazať"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
              <span className="absolute bottom-2 left-2 text-[11px] bg-black/50 px-1.5 py-0.5 rounded text-white/80">#{i + 1}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
